import { IGetWarehouseDocumentsParams, IGetWarehouseSkusDataParams, IWarehousesParams } from '@app/models/interfaces';
import {
  ContextSchema,
  CreateWarehouseSchema,
  DocumentFilterOptionSchema,
  DocumentKind,
  ReceiptDocumentSchema,
  RecordsListGenericSchemaAnnotatedUnionShipmentDocumentListItemSchemaReceiptDocumentListItemSchemaFieldInfoAnnotationNoneTypeRequiredTrueDiscriminatorKind as RecordsListGenericSchemaRetrieveDocumentListItemSchema,
  RecordsListGenericSchemaRetrieveWarehouseSchema,
  RecordsListGenericSchemaRetrieveWarehouseSkuSchema,
  RetrieveWarehouseSchema,
  RetrieveWarehouseSettingsSchema,
  ShipmentDocumentSchema,
  UpdateWarehouseSchema,
  WarehouseCustomerSchema,
  WarehouseImportErrorsCountSchema,
} from '@app/models/wms';
import { handleRequest, httpClient } from '@app/services/httpClient';
import { objectToSearchParams } from '@app/services/objectToSearchParams';

import { IProps } from '../interfaces';

type RetrieveDocumentSchema = ShipmentDocumentSchema | ReceiptDocumentSchema;

class WMS {
  private readonly endPoint: string;

  constructor({ baseEndPoint }: IProps) {
    this.endPoint = baseEndPoint;
  }

  async postWarehouse(data: CreateWarehouseSchema): Promise<RetrieveWarehouseSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/warehouses`, data));
  }

  async getWarehouses(params: IWarehousesParams): Promise<RecordsListGenericSchemaRetrieveWarehouseSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/warehouses${objectToSearchParams(params)}`));
  }

  async getWarehouseCustomers(): Promise<WarehouseCustomerSchema[]> {
    return handleRequest(httpClient.get(`${this.endPoint}/warehouses/customers`));
  }

  async getWarehouse(uid: string): Promise<RetrieveWarehouseSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/warehouses/${uid}`));
  }

  async getWarehouseSettings(uid: string): Promise<RetrieveWarehouseSettingsSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/warehouses/${uid}/settings`));
  }

  async putWarehouse(data: UpdateWarehouseSchema, uid: string): Promise<RetrieveWarehouseSchema> {
    return handleRequest(httpClient.put(`${this.endPoint}/warehouses/${uid}`, data));
  }

  async deactivateWarehouse(uid: string): Promise<RetrieveWarehouseSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/warehouses/${uid}/deactivate`));
  }

  async reactivateWarehouse(uid: string): Promise<RetrieveWarehouseSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/warehouses/${uid}/reactivate`));
  }

  async getContext(): Promise<ContextSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/context`));
  }

  async getWarehouseSkus({
    warehouse_uid,
    ...params
  }: IGetWarehouseSkusDataParams): Promise<RecordsListGenericSchemaRetrieveWarehouseSkuSchema> {
    return handleRequest(
      httpClient.get(`${this.endPoint}/warehouses/${warehouse_uid}/skus${objectToSearchParams(params)}`)
    );
  }

  async getWarehouseSkusImportErrorCount(warehouse_uid: string): Promise<WarehouseImportErrorsCountSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/warehouses/${warehouse_uid}/skus/import-errors-count`));
  }

  async getWarehouseDocumentsRecipients(
    warehouse_uid: string,
    kind: DocumentKind
  ): Promise<DocumentFilterOptionSchema[]> {
    return handleRequest(
      httpClient.get(`${this.endPoint}/warehouses/${warehouse_uid}/documents/recipients?kind=${kind}`)
    );
  }

  async getWarehouseDocumentsSenders(warehouse_uid: string, kind: DocumentKind): Promise<DocumentFilterOptionSchema[]> {
    return handleRequest(httpClient.get(`${this.endPoint}/warehouses/${warehouse_uid}/documents/senders?kind=${kind}`));
  }

  async getWarehouseDocumentsStatuses(
    warehouse_uid: string,
    kind: DocumentKind
  ): Promise<DocumentFilterOptionSchema[]> {
    return handleRequest(
      httpClient.get(`${this.endPoint}/warehouses/${warehouse_uid}/documents/statuses?kind=${kind}`)
    );
  }

  async getWarehouseDocuments({
    warehouse_uid,
    ...params
  }: IGetWarehouseDocumentsParams): Promise<RecordsListGenericSchemaRetrieveDocumentListItemSchema> {
    return handleRequest(
      httpClient.get(`${this.endPoint}/warehouses/${warehouse_uid}/documents${objectToSearchParams(params)}`)
    );
  }

  async getDocument(document_uid: string): Promise<RetrieveDocumentSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/documents/${document_uid}`));
  }
}

export default WMS;
